import * as Sentry from '@sentry/browser';

/** אתחול ניטור שגיאות. no-op אם VITE_SENTRY_DSN לא מוגדר (למשל dev מקומי). */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    // ⚠️ `integrations: []` **אינו** מבטל את ברירות המחדל — ‏@sentry/core ממזג
    // (`[...defaultIntegrations, ...userIntegrations]`). כדי לבטל צריך `defaultIntegrations: false`.
    // ההערה שהייתה כאן ("לא לאסוף התנהגות משתמשת") תיארה כוונה שלא התקיימה במשך שנה.
    integrations: [],
    tracesSampleRate: 0,
    // ⚠️ `integrations: []` אינו מבטל את ברירות המחדל — ‏Breadcrumbs פעיל, וכל אירוע נושא את הלחיצות
    // האחרונות (ה-SDK מסריאל את האלמנט **כולל aria-label**, שאצלנו הוא "הסרת <שם מאכל>") ואת כתובות
    // ה-fetch המלאות (כולל מחרוזת החיפוש ב-`name_he=ilike.*…*`). זה תוכן יומן. משאירים מ-fetch רק
    // נתיב+סטטוס — זה מה שצריך לאבחון רשת — ומוחקים פירורי UI וקונסול. (נתפס בביקורת 16.09.2026.)
    // ⚠️ שגיאה שנזרקת כאובייקט PostgREST ואינה נתפסת מגיעה ל-Sentry כ"Non-Error exception"
    // וה-SDK מסריאל את האובייקט כולו ל-`extra` — כולל `details` ("Failing row contains (…, קפה
    // שחור, …)"). ‏`reportError` שלנו לא משתמש ב-extra בכלל (רק tags), ולכן המחיקה בטוחה
    // וסוגרת את הקטגוריה במקום להסתמך על משמעת בכל catch עתידי.
    beforeSend(event) {
      delete event.extra;
      return event;
    },
    beforeBreadcrumb(crumb) {
      const cat = crumb.category ?? '';
      if (cat.startsWith('ui.') || cat === 'console') return null;
      if ((cat === 'fetch' || cat === 'xhr') && crumb.data && typeof crumb.data.url === 'string') {
        crumb.data.url = crumb.data.url.split('?')[0];
      }
      return crumb;
    },
  });
}

/**
 * דיווח שגיאה שנתפסה ב-catch. ‏Sentry אוסף לבד רק שגיאות שלא נתפסו, ולכן כשלי
 * שמירה/מחיקה נבלעו לגמרי — וכשהמשתמשת דיווחה "צריך ללחוץ כמה פעמים" לא הייתה שום
 * ראיה לומר למה (16.09.2026).
 * ⚠️ נשלח **מטא-דאטה טכני בלבד** — קוד שגיאה, סטטוס HTTP, שם הפעולה. לעולם לא `message`/`details`
 * של השגיאה: הודעות PostgREST על הפרת אילוץ מכילות ערכי שדות (שמות מאכלים). החריג היחיד —
 * סנטינל פנימי שלנו (`no_session`, `not_found`), שמזוהה לפי צורה קשיחה (אותיות קטנות וקו תחתון
 * בלבד) ולא לפי רשימה; שום הודעת דפדפן/PostgREST אינה עוברת את הצורה הזו.
 * הערכים הולכים ל-**tags** (ניתנים לסינון ולפילוח ב-Sentry; `extra` אינו), וה-fingerprint מפריד
 * לפי פעולה+סוג — כך שסוג כשל **חדש** (קוד שלא ראינו) פותח issue חדש ולא נבלע ברעש הרשת.
 */
export function reportError(e: unknown, ctx: { action: string; transient?: boolean }): void {
  const o = (typeof e === 'object' && e !== null ? e : {}) as { code?: unknown; status?: unknown; name?: unknown; message?: unknown };
  const code = typeof o.code === 'string' && /^[A-Za-z0-9_]{1,16}$/.test(o.code) ? o.code : null;
  const status = typeof o.status === 'number' ? o.status : null;
  const name = typeof o.name === 'string' && /^[A-Za-z_]{1,48}$/.test(o.name) ? o.name : null;
  const sentinel = typeof o.message === 'string' && /^[a-z_]{1,32}$/.test(o.message) ? o.message : null;
  const kind = ctx.transient ? 'transient' : code ?? sentinel ?? (status !== null ? `http_${status}` : name ?? 'unknown');
  try {
    Sentry.captureMessage(`caught:${ctx.action}`, {
      level: 'warning',
      fingerprint: ['caught', ctx.action, kind],
      tags: {
        action: ctx.action,
        kind,
        code: code ?? '',
        status: status !== null ? String(status) : '',
        err_name: name ?? '',
        transient: ctx.transient === undefined ? '' : String(ctx.transient),
      },
    });
  } catch { /* ניטור לא מפיל פעולה */ }
}
